import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Tippy from '@tippy.js/react'
import 'react-tippy/dist/tippy.css'
import Swal from 'sweetalert2'
import { BiTrash, BiEdit } from 'react-icons/bi'
import {
	DefaultButton,
	DefaultPageTitle,
	DefaultTable,
	Loading,
	ErrorUI,
} from 'components'
import { getAllComments, deleteComment } from 'services/comments'
import Comment from 'models/comment'
import { DefaultCreateButtonAndSearchFormContainer, SearchContainer, CommentsContainer } from './style'
import DefaultInput from 'components/DefaultInput'
import { CREATE_COMMENT, EDIT_COUPON_BY_ID } from 'components/Routes/Constants'
import { formatDate } from 'helpers/format-date'

const useCOUPONS = () => {
	const [comments, setComments] = useState<Comment[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(false)

	const getComments = useCallback(async () => {
		try {
			setLoading(true)
			const commentsReponse = await getAllComments()
			if (commentsReponse && commentsReponse.length) {
				setComments(commentsReponse)
			}
			setError(false)
		} catch (err) {
			setError(true)
		}
		setLoading(false)
		
	}, [setComments])

	const handleDeleteComment = useCallback(
		async ({ comment_id }: Comment) => {
			setLoading(true)
			const responseFire = await Swal.fire({
				title: '<strong>Confirmação</strong>',
				html: `Tem certeza que deseja remover este comentário?`,
				showCancelButton: true,
				cancelButtonText: 'Cancelar',
				focusConfirm: false,
			})
			if (responseFire.isConfirmed) {
				await deleteComment(comment_id)

				Swal.fire({
					icon: 'success',
					title: 'Sucesso!',
					text: `O comentário foi excluido com sucesso!`,
				})

				await getComments()
			}
			setLoading(false)
		},
		[getComments]
	)

	useEffect(() => {
		getComments()
	}, [getComments])

	return { comments, loading, error, handleDeleteComment }
}

const CommentsUI: React.FC = () => {
	const history = useHistory()
	const { comments, loading, error, handleDeleteComment } = useCOUPONS()
	const [filter, setFilter] = useState('')

	const commentFilter = useMemo(() => {
		const lowerCase = filter.toLowerCase()
		return comments.filter(
			(comment) =>
				comment.description.toLowerCase().includes(lowerCase) || comment.description.toLowerCase().includes(lowerCase)
		)
	}, [filter, comments])

	const createComment = async () => {
		history.push(`${CREATE_COMMENT}`)
	}

	const editComment = useCallback(
		(commentId: string) => {
			const editCommentRoute = EDIT_COUPON_BY_ID(commentId)
			history.push(editCommentRoute)
		},
		[history]
	)

	const CommentsToBeShown = useMemo(
		() =>
			(commentFilter || [])
				.sort((u1, u2) => u2.created_at.localeCompare(u1.created_at))
				.map((comment) => ({
					id: comment.comment_id,
					username: comment.username,
					date: formatDate(comment.created_at),
					description: comment.description,
					trail_id: comment.trail_id,
					course_id: comment.course_id,
					reference_id: comment.reference_id,
					actions: (
						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
							<Tippy content="Responder comentário">
								<DefaultButton
								className="small info"
								onClick={() => {
									if (comment.trail_id && comment.course_id && comment.reference_id) {
										window.open(
											`https://www.cursodegestanteregenesis.com.br/trilha/${comment.trail_id}/curso/${comment.course_id}/aula/${comment.reference_id}`,
											'_blank'
          								);

									}
								}}
								>
								<BiEdit />
								</DefaultButton>
							</Tippy>
							<Tippy content="Excluir comentário">
								<DefaultButton
									style={{ marginLeft: '10px' }}
									className="small danger"
									onClick={() => handleDeleteComment(comment)}
								>
									<BiTrash />
								</DefaultButton>
							</Tippy>
						</div>
					),
				})),
		[commentFilter, deleteComment]
	)

	if (loading) {
		return <Loading />
	}

	if (error) {
		return <ErrorUI type="loadingError" />
	}

	return (
		<CommentsContainer className="comments-list">
			<DefaultPageTitle>Lista de Comentários</DefaultPageTitle>
			{/* <CreateButtonDefaultContainer>
				<DefaultButton onClick={createComment}>Criar Comentário</DefaultButton>
			</CreateButtonDefaultContainer> */}
			<SearchContainer>
				<DefaultCreateButtonAndSearchFormContainer style={{ justifyContent: 'flex-end' }}>
					<DefaultInput
						type="search"
						placeholder="Digite sua pesquisa aqui..."
						value={filter}
						onChange={(e) => setFilter(e.target.value)}
						style={{ margin: 0 }}
					/>
				</DefaultCreateButtonAndSearchFormContainer>
			</SearchContainer>
			<DefaultTable
				headersConfig={[
					{
						headerLabel: <span>Usuário</span>,
						propName: 'username',
					},
					{
						headerLabel: <span>Descrição</span>,
						propName: 'description',
					},
					{
						headerLabel: <span>Data</span>,
						propName: 'date',
					},
					{
						headerLabel: <span>Ações</span>,
						propName: 'actions',
					},
				]}
				items={CommentsToBeShown}
				emptyListMessage={'Não foram encontrados comentários cadastrados!'}
			/>
		</CommentsContainer>
	)
}

export default CommentsUI
